import { useState } from 'react'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import {
  COMMON_BRANDS,
  FEE_ROWS,
  DEFAULT_PIX_FEE,
  MAX_BRAND_NAME,
  emptyFees,
  normalizeFees,
  pixFeeOf,
  withPixFee,
  brandLabel,
  brandFromText,
  addBrand,
  removeBrand,
  getFee,
  setFee,
  parsePercentage,
  formatPercentage,
  editablePercentage,
  CREDIT_MODES,
  normalizeSettlement,
  maxCreditDays,
  parseDays,
  MAX_SHORT_DAYS
} from 'services/CardMachine'
import cardMachineValidator from 'validators/cardMachineValidator'
import TextInput from 'components/Inputs/TextInput'
import CheckBox from 'components/CheckBox'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'

import {
  Card,
  Field,
  Label,
  BrandGroup,
  BrandRow,
  OtherBrandRow,
  HintText,
  WarningText,
  TableWrapper,
  Table,
  HeaderCell,
  LabelCell,
  ValueCell,
  FeeInput,
  TermRow,
  TermInput,
  TermSelect,
  Actions,
  ActionButton
} from './styles'

// Mostra "Grátis"/"1,5%" fora de foco e o número cru enquanto se digita.
function FeeCell({ id, value, disabled, onChange: handleChange }) {
  const [draft, setDraft] = useState(null)

  const handleInput = event => {
    setDraft(event.target.value)

    const parsed = parsePercentage(event.target.value)
    if (!Number.isNaN(parsed)) handleChange(parsed)
  }

  return (
    <FeeInput
      id={id}
      inputMode='decimal'
      value={draft ?? formatPercentage(value)}
      disabled={disabled}
      onFocus={() => setDraft(editablePercentage(value))}
      onBlur={() => setDraft(null)}
      onChange={handleInput}
    />
  )
}

// Dias inteiros, com o número cru enquanto se digita; apagar tudo e sair devolve o valor anterior.
function DaysCell({ id, value, max, disabled, onChange: handleChange }) {
  const [draft, setDraft] = useState(null)

  const handleInput = event => {
    setDraft(event.target.value)

    const parsed = parseDays(event.target.value)
    if (parsed !== null && !Number.isNaN(parsed) && parsed <= max) handleChange(parsed)
  }

  return (
    <TermInput
      id={id}
      inputMode='numeric'
      value={draft ?? String(value)}
      disabled={disabled}
      onBlur={() => setDraft(null)}
      onChange={handleInput}
    />
  )
}

export default function CardMachineForm({ machine, canEdit, onSaved: handleSaved, onDeleted: handleDeleted, onCancel: handleCancel }) {
  const businessId = AuthService.getBusinessId()

  const initialBrands = machine?.brands ?? []

  const [name, setName] = useState(machine?.name ?? '')
  const [brands, setBrands] = useState(initialBrands)
  const [fees, setFees] = useState(machine ? normalizeFees(machine.fees, initialBrands) : emptyFees())
  const [pixFee, setPixFee] = useState(machine ? pixFeeOf(machine.fees, initialBrands) : DEFAULT_PIX_FEE)
  // Nulo = a maquininha não informou o prazo e a projeção usa o padrão; mexer em qualquer campo grava os três.
  const [settlement, setSettlement] = useState(machine?.settlement ?? null)
  const [otherOpen, setOtherOpen] = useState(false)
  const [otherText, setOtherText] = useState('')
  const [saving, setSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)

  // As comuns sempre aparecem; as que o salão digitou, só depois de adicionadas.
  const customBrands = brands.filter(brand => !COMMON_BRANDS.some(common => common.value === brand))
  const brandOptions = [...COMMON_BRANDS.map(common => common.value), ...customBrands]

  const term = normalizeSettlement(settlement)

  const changeSettlement = change => setSettlement({ ...term, ...change })

  const changeCredit = change => changeSettlement({ credit: { ...term.credit, ...change } })

  const changeCreditMode = mode => changeCredit({ mode, days: Math.min(term.credit.days, maxCreditDays(mode)) })

  const applyBrands = result => {
    setBrands(result.brands)
    setFees(result.fees)
  }

  const toggleTableBrand = (brand, checked) => {
    if (checked) applyBrands(addBrand(brands, fees, brand))
    else applyBrands(removeBrand(brands, fees, [], brand))
  }

  const addOtherBrand = () => {
    const brand = brandFromText(otherText)
    if (!brand) return

    applyBrands(addBrand(brands, fees, brand))
    setOtherText('')
  }

  const handleOtherKeyDown = event => {
    if (event.key !== 'Enter') return

    // Dentro do formulário, Enter enviaria o cadastro em vez de adicionar a bandeira.
    event.preventDefault()
    addOtherBrand()
  }

  const save = async event => {
    event.preventDefault()

    // A taxa do Pix é um campo só na tela, mas a API e os apps mobile ainda esperam
    // `fees[bandeira].pix`: carimba o mesmo valor em todas as bandeiras antes de validar/enviar.
    const feesToSave = withPixFee(fees, pixFee)

    try {
      setErrorMessage(null)
      await cardMachineValidator.validate({ name, fees: feesToSave, settlement })
    } catch (error) {
      setErrorMessage(error.message)
      return
    }

    try {
      setSaving(true)

      const body = { name: name.trim(), brands, linkedBrands: [], fees: feesToSave, settlement }
      const requestAuth = AuthService.getRequestAuth()
      const url = `/businesses/${businessId}/card_machines`

      const { data } = machine
        ? await cutileiApi.put(`${url}/${machine.id}`, body, requestAuth)
        : await cutileiApi.post(url, body, requestAuth)

      handleSaved(data)
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    try {
      setSaving(true)
      setErrorMessage(null)

      await cutileiApi.delete(
        `/businesses/${businessId}/card_machines/${machine.id}`, AuthService.getRequestAuth()
      )

      handleDeleted(machine)
    } catch (error) {
      logApiError(error)
      setErrorMessage(getApiErrorMessage(error))
      setSaving(false)
    }
  }

  const idPrefix = `card-machine-${machine ? (machine.id ?? 'unregistered') : 'new'}`

  return (
    <Card onSubmit={save}>
      <Field>
        <Label htmlFor={`${idPrefix}-name`}>Nome</Label>
        <TextInput
          id={`${idPrefix}-name`}
          placeholder='Nome da maquininha'
          value={name}
          disabled={!canEdit}
          onChange={event => setName(event.target.value)}
        />
      </Field>
      <Field>
        <Label htmlFor={`${idPrefix}-pix-fee`}>Taxa do Pix</Label>
        <HintText>Taxa única da maquininha: não depende da bandeira do cartão.</HintText>
        <FeeCell
          id={`${idPrefix}-pix-fee`}
          value={pixFee}
          disabled={!canEdit}
          onChange={setPixFee}
        />
      </Field>
      <Field>
        <Label>Bandeiras que a maquininha aceita</Label>
        <HintText>Cada bandeira marcada ganha uma coluna na tabela de taxas.</HintText>
        <BrandGroup>
          {brandOptions.map(brand => (
            <BrandRow key={brand}>
              <CheckBox
                id={`${idPrefix}-brand-${brand}`}
                value={brands.includes(brand)}
                disabled={!canEdit}
                onValueChange={checked => toggleTableBrand(brand, checked)}
              />
              <label htmlFor={`${idPrefix}-brand-${brand}`}>{brandLabel(brand)}</label>
            </BrandRow>
          ))}
          <BrandRow>
            <CheckBox
              id={`${idPrefix}-brand-other`}
              value={otherOpen}
              disabled={!canEdit}
              onValueChange={setOtherOpen}
            />
            <label htmlFor={`${idPrefix}-brand-other`}>Outro</label>
          </BrandRow>
        </BrandGroup>
        {otherOpen && canEdit && (
          <OtherBrandRow>
            <TextInput
              id={`${idPrefix}-other-brand`}
              placeholder='Nome da bandeira'
              maxLength={MAX_BRAND_NAME}
              value={otherText}
              onChange={event => setOtherText(event.target.value)}
              onKeyDown={handleOtherKeyDown}
            />
            <ActionButton type='button' onClick={addOtherBrand}>Adicionar</ActionButton>
          </OtherBrandRow>
        )}
      </Field>
      {brands.length === 0 ? (
        <HintText>Escolha uma bandeira para informar as taxas.</HintText>
      ) : (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th/>
                {brands.map(brand => (
                  <HeaderCell key={brand}>
                    {brandLabel(brand)}
                  </HeaderCell>
                ))}
              </tr>
            </thead>
            <tbody>
              {FEE_ROWS.map(row => (
                <tr key={row.id}>
                  <LabelCell>{row.label}</LabelCell>
                  {brands.map(brand => (
                    <ValueCell key={brand}>
                      <FeeCell
                        id={`${idPrefix}-${brand}-${row.id}`}
                        value={getFee(fees, brand, row)}
                        disabled={!canEdit}
                        onChange={value => setFees(setFee(fees, brand, row, value))}
                      />
                    </ValueCell>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      )}
      <Field>
        <Label>Prazo de recebimento</Label>
        <HintText>
          Em quantos dias corridos depois da venda a maquininha deposita o valor. Usado na estimativa de
          recebimentos do fluxo de caixa. {settlement ? '' : 'Ainda não informado: a estimativa usa o padrão.'}
        </HintText>
        <TermRow>
          <label htmlFor={`${idPrefix}-term-pix`}>Pix</label>
          <DaysCell
            id={`${idPrefix}-term-pix`}
            value={term.pix}
            max={MAX_SHORT_DAYS}
            disabled={!canEdit}
            onChange={days => changeSettlement({ pix: days })}
          />
          <HintText>dia(s) · 0 = no mesmo dia, 1 = no dia seguinte</HintText>
        </TermRow>
        <TermRow>
          <label htmlFor={`${idPrefix}-term-debit`}>Débito</label>
          <DaysCell
            id={`${idPrefix}-term-debit`}
            value={term.debit}
            max={MAX_SHORT_DAYS}
            disabled={!canEdit}
            onChange={days => changeSettlement({ debit: days })}
          />
          <HintText>dia(s)</HintText>
        </TermRow>
        <TermRow>
          <label htmlFor={`${idPrefix}-term-credit-mode`}>Crédito</label>
          <TermSelect
            id={`${idPrefix}-term-credit-mode`}
            value={term.credit.mode}
            disabled={!canEdit}
            onChange={event => changeCreditMode(event.target.value)}
          >
            {CREDIT_MODES.map(mode => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
          </TermSelect>
          <DaysCell
            id={`${idPrefix}-term-credit-days`}
            value={term.credit.days}
            max={maxCreditDays(term.credit.mode)}
            disabled={!canEdit}
            onChange={days => changeCredit({ days })}
          />
          <HintText>dia(s)</HintText>
        </TermRow>
      </Field>
      {errorMessage && <WarningText>{errorMessage}</WarningText>}
      {canEdit && (
        <Actions>
          <ActionButton type='submit' disabled={saving}>
            {saving ? <ButtonLoading/> : 'Salvar'}
          </ActionButton>
          <ActionButton type='button' $secondary disabled={saving} onClick={handleCancel}>Cancelar</ActionButton>
          {machine && (
            <ActionButton type='button' $danger disabled={saving} onClick={remove}>Excluir</ActionButton>
          )}
        </Actions>
      )}
    </Card>
  )
}
